import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert } from 'reactstrap';
import useInventoryDiscoverScan from './useInventoryDiscoverScan';

/**
 * Runs the async CMDB discovery scan for a vendor with no managed product.
 *
 * Opens PAUSED: reaching into a customer's ServiceNow must never happen as a
 * side effect of a click, so nothing starts until the user clicks Scan.
 * onComplete receives the discovered products array.
 */
const InventoryScanModal = ({ isOpen, vendorId, onComplete, onCancel }) => {
  const { status, result, error, start, cancel, reset } = useInventoryDiscoverScan();
  const completedRef = useRef(false);
  const wasOpenRef = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      completedRef.current = false;
      reset();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, reset]);

  useEffect(() => {
    if (status === 'succeeded' && result && !completedRef.current) {
      completedRef.current = true;
      onComplete(result.products || []);
    }
  }, [status, result, onComplete]);

  const handleCancel = () => {
    cancel();
    onCancel();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="lg" backdrop="static">
      <ModalHeader toggle={handleCancel}>Scan ServiceNow inventory</ModalHeader>
      <ModalBody>
        {status === 'idle' && (
          <>
            <p>
              BugZero will read your ServiceNow CMDB to list the products installed for this vendor. This can take a
              minute.
            </p>
            <p className="text-muted mb-0">
              <em>Read-only &mdash; nothing in ServiceNow is modified.</em>
            </p>
          </>
        )}
        {status === 'scanning' && (
          <div className="text-center my-4">
            <Spinner color="primary" />
            <p className="mt-3 mb-0">Scanning your ServiceNow inventory&hellip;</p>
            <p className="text-muted">This can take a minute for large inventories.</p>
          </div>
        )}
        {status === 'failed' && <Alert color="danger">{error}</Alert>}
      </ModalBody>
      <ModalFooter>
        {status === 'idle' && (
          <Button color="primary" onClick={() => start(vendorId)}>
            Scan ServiceNow inventory
          </Button>
        )}
        {status === 'failed' && (
          <Button color="primary" onClick={() => start(vendorId)}>
            Try again
          </Button>
        )}
        <Button color="secondary" outline onClick={handleCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

InventoryScanModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  vendorId: PropTypes.string,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default InventoryScanModal;
